import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Chip,
} from '@material-tailwind/react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  // CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { UserAuth } from '../../context/AuthContext';
import {
  getCirrusSpecsStatus,
  uploadCirrusSpecs,
  getCirrusSpecsVersions,
} from '../../services/cirrusSpecsApi';

function CirrusConfiguratorPage() {
  const { isAdmin } = UserAuth();
  const fileInputRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Load status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  // Load versions for admin users
  useEffect(() => {
    if (isAdmin) {
      loadVersions();
    }
  }, [isAdmin]);

  const loadStatus = async () => {
    try {
      setIsLoadingStatus(true);
      setError(null);
      const data = await getCirrusSpecsStatus();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const loadVersions = async () => {
    try {
      setIsLoadingVersions(true);
      const data = await getCirrusSpecsVersions();
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setSuccessMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setError(null);
      setSuccessMessage(null);

      const csvContent = await selectedFile.text();
      const result = await uploadCirrusSpecs(csvContent);

      setSuccessMessage(`Successfully uploaded ${result.rowCount.toLocaleString()} rows`);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh status and versions
      loadStatus();
      if (isAdmin) {
        loadVersions();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12">
            <XCircleIcon className="h-12 w-12 text-red-500" />
            <Typography variant="h5" color="red">
              Access Denied
            </Typography>
            <Typography color="gray">
              Admin access is required to view this page.
            </Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Typography variant="h4" color="blue-gray">
        Cirrus Configurator
      </Typography>
      <Typography color="gray" className="-mt-4">
        Upload and manage Cirrus aircraft specification data
      </Typography>

      {error && (
        <Alert color="red" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="green" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Status Card */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              <Typography variant="h6" color="blue-gray">
                Current Status
              </Typography>
            </div>
            <Button
              variant="text"
              size="sm"
              className="flex items-center gap-1"
              onClick={loadStatus}
              disabled={isLoadingStatus}
            >
              <ArrowPathIcon className={`h-4 w-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {isLoadingStatus ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : status?.configured ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Typography variant="small" color="gray" className="font-medium">
                  Status
                </Typography>
                <Chip
                  size="sm"
                  value="Configured"
                  color="green"
                  className="mt-1"
                />
              </div>
              <div>
                <Typography variant="small" color="gray" className="font-medium">
                  Row Count
                </Typography>
                <Typography variant="h6" color="blue-gray">
                  {status.rowCount?.toLocaleString() || 'N/A'}
                </Typography>
              </div>
              <div>
                <Typography variant="small" color="gray" className="font-medium">
                  Last Updated
                </Typography>
                <Typography variant="small" color="blue-gray">
                  {formatDate(status.uploadedAt)}
                </Typography>
              </div>
              <div>
                <Typography variant="small" color="gray" className="font-medium">
                  Version
                </Typography>
                <Typography variant="small" color="blue-gray" className="font-mono text-xs">
                  {status.latestVersion || 'N/A'}
                </Typography>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 py-4">
              <XCircleIcon className="h-8 w-8 text-gray-400" />
              <div>
                <Typography color="gray">Not Configured</Typography>
                <Typography variant="small" color="gray">
                  No Cirrus configurator CSV has been uploaded yet
                </Typography>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-2 mb-4">
            <CloudArrowUpIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              Upload CSV
            </Typography>
          </div>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <CloudArrowUpIcon className="h-10 w-10 text-gray-400" />
                <Typography color="gray">
                  Click to select a CSV file
                </Typography>
                <Typography variant="small" color="gray">
                  Required headers: Model, Serial #, N #
                </Typography>
              </label>
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="h-6 w-6 text-blue-500" />
                  <div>
                    <Typography variant="small" className="font-medium">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="small" color="gray">
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="text"
                  color="red"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Remove
                </Button>
              </div>
            )}

            <Button
              className="flex items-center justify-center gap-2"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              fullWidth
            >
              {isUploading ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <CloudArrowUpIcon className="h-5 w-5" />
              )}
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Version History */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-2 mb-4">
            <ClockIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              Version History
            </Typography>
          </div>

          {isLoadingVersions ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-6 w-6" />
            </div>
          ) : versions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max table-auto text-left">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3">
                      <Typography variant="small" className="font-semibold text-gray-700">
                        Version
                      </Typography>
                    </th>
                    <th className="p-3">
                      <Typography variant="small" className="font-semibold text-gray-700">
                        Uploaded
                      </Typography>
                    </th>
                    <th className="p-3">
                      <Typography variant="small" className="font-semibold text-gray-700">
                        Rows
                      </Typography>
                    </th>
                    <th className="p-3">
                      <Typography variant="small" className="font-semibold text-gray-700">
                        Uploaded By
                      </Typography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v, index) => (
                    <tr key={v.version} className="border-b">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Typography variant="small" className="font-mono text-xs">
                            {v.version}
                          </Typography>
                          {index === 0 && (
                            <Chip size="sm" value="Latest" color="green" variant="ghost" />
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Typography variant="small" color="gray">
                          {formatDate(v.uploadedAt)}
                        </Typography>
                      </td>
                      <td className="p-3">
                        <Typography variant="small">
                          {v.rowCount?.toLocaleString() || 'N/A'}
                        </Typography>
                      </td>
                      <td className="p-3">
                        <Typography variant="small" color="gray" className="font-mono text-xs">
                          {v.uploadedBy || 'N/A'}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Typography color="gray">No version history available</Typography>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default CirrusConfiguratorPage;
